"use client";

import { AssetBlok } from "@/components/bloks/Asset";
import { useAccountModal } from "@/features/account/AccountModal";
import type { GlobalData } from "@/features/global/dal";
import { type Currency, currencyToPoints } from "@/features/loyalty/conversion-rate";
import { useEarningRules } from "@/features/loyalty/hooks";
import { useCurrency } from "@/hooks/useCurrency";
import { useFlag } from "@/lib/flags/client";
import { useLoggedIn } from "@frend-digital/centra/client";
import type { ReactNode } from "react";
import styles from "./PdpUsps.module.css";

type PdpUspsEntry = GlobalData["pdpUsps"][number];

export interface PdpUspsProps {
	entries: GlobalData["pdpUsps"];
	price?: number;
}

// Editors author two entries in Storyblok - one per `loggedIn` state - with
// identical fields. Render both; each self-selects via the guard clause
// below, same pattern as MemberBannerBlok.
export const PdpUsps = ({ entries, price }: PdpUspsProps) => (
	<>
		{entries.map((entry, index) => (
			// biome-ignore lint/suspicious/noArrayIndexKey: fixed, order-only list of mutually exclusive entries
			<PdpUspsEntryView key={index} entry={entry} price={price} />
		))}
	</>
);

const POINTS_TOKEN = "{{points}}";

const renderPointsText = (text: string | undefined, points: number | undefined): ReactNode => {
	if (!text) return undefined;
	if (!text.includes(POINTS_TOKEN)) return text;
	if (points === undefined) return undefined; // hide the row until the live count resolves

	const [prefix, suffix] = text.split(POINTS_TOKEN);
	return (
		<>
			{prefix}
			<strong>{points}</strong>
			{suffix}
		</>
	);
};

const PdpUspsEntryView = ({ entry, price }: { entry: PdpUspsEntry; price?: number }) => {
	const { data: rawLoggedIn } = useLoggedIn();
	const [, setOpen] = useAccountModal();
	const { data: currency } = useCurrency();
	const loyaltyEnabled = useFlag("loyalty.enabled");
	const { data: earningRules } = useEarningRules();

	// `loggedIn` is `undefined` until the auth check resolves. Default to the
	// logged-out entry in that window (matches guests and the pending state
	// alike) and only reveal the logged-in entry once it's confirmed, so the
	// two Storyblok entries never render together on load.
	const loggedIn = rawLoggedIn === true;
	if (entry.loggedIn === true && !loggedIn) return null;
	if (entry.loggedIn === false && loggedIn) return null;

	const points =
		loyaltyEnabled && price && earningRules
			? currencyToPoints((currency as Currency) || "NOK", price, earningRules.pointsPerUnit)
			: undefined;

	const items = [
		{
			icon: entry.deliveryIcon,
			text: entry.deliveryText,
			suffix: entry.deliverySuffix as string | undefined,
		},
		{
			icon: entry.returnsIcon,
			text: entry.returnsText,
			suffix: entry.returnsSuffix as string | undefined,
		},
		{
			icon: entry.pointsIcon,
			text: renderPointsText(entry.pointsText, points),
			suffix: undefined,
		},
	].filter((item) => !!item.text);

	if (!items.length) return null;

	const ctaMode = entry.ctaMode || "register";

	return (
		<div className={styles.root}>
			{items.map((item, index) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: fixed, order-only list of USP rows
				<div key={index} className={styles.item}>
					<span className={styles.icon}>
						{item.icon?.filename && (
							<span className={styles.iconImage}>
								<AssetBlok source={item.icon} sizes="18px" />
							</span>
						)}
					</span>
					<span className={styles.text}>
						{item.text}
						{item.suffix && (
							<>
								{" - "}
								<span className={styles.muted}>{item.suffix}</span>
							</>
						)}
					</span>
				</div>
			))}

			{!loggedIn && (entry.eyebrowText || entry.ctaLabel) && (
				<div className={styles.signUp}>
					{entry.eyebrowText && <span className={styles.signUpEyebrow}>{entry.eyebrowText}</span>}
					{entry.ctaLabel && (
						<button
							type="button"
							className={styles.signUpLink}
							onClick={() => setOpen(true, ctaMode)}
						>
							{entry.ctaLabel}
						</button>
					)}
				</div>
			)}
		</div>
	);
};
